import { VideoResponsePlayback } from "@vericus/cadmus-common";
import { Button, Spacer, Text } from "@vericus/cadmus-ui";

import { Overlay } from "@/components/device-check/overlay";
import type { UseWebcamCaptureReturn } from "@/components/webcam-capture";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppSelector } from "@/data/hooks";
import { selectCameraRequiredDisabled } from "@/features/socratic/socratic-slice";
import type { CountdownMessage } from "@/features/timeline/use-countdown/countdown-message";
import { useTrackOnce } from "@/hooks/use-track-once";

import { SilentAudioNudge } from "./silent-audio-nudge";
import * as styles from "./socratic-video-capture.css";

interface Props {
  webcam: UseWebcamCaptureReturn;
  preparationCountdown: CountdownMessage | null;
  dialogueId: string;
  showSilentAudioNudge?: boolean;
}

/**
 * Minimal video display for Socratic dialogue.
 */
export function SocraticVideoCapture({
  webcam,
  preparationCountdown,
  dialogueId,
  showSilentAudioNudge = false,
}: Props) {
  const trackOnce = useTrackOnce();

  const handleVideoPlay = () => {
    trackOnce(JitsuEvent.VIDEO_RESPONSE_REVIEWED, { dialogue_id: dialogueId });
  };

  const cameraRequiredDisabled = useAppSelector(selectCameraRequiredDisabled);
  const cameraToggleDisabled = webcam.isRecording || !cameraRequiredDisabled;

  // Browser Support Error State
  if (!webcam.browserSupport.supported) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text kind="bodyLg" color="failure">
            {webcam.browserSupport.error}
          </Text>
          <Spacer spacing={12} />
          <Text kind="bodySm">
            Please update your browser or use a different one to access camera
            features.
          </Text>
        </div>
      </div>
    );
  }

  // Recorded Video Playback
  if (webcam.capturedMedia) {
    return (
      <VideoResponsePlayback
        url={webcam.capturedMedia.url}
        className={styles.recordedVideoWrapper}
        onPlay={handleVideoPlay}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.videoWrapper}>
        {webcam.cameraEnabled && (
          <video
            ref={webcam.videoRef}
            autoPlay
            playsInline
            muted
            className={styles.video}
            style={{ transform: "scaleX(-1)" }}
          />
        )}

        {!webcam.cameraEnabled && preparationCountdown === null && (
          <div className={styles.cameraOff}>
            <Text kind="bodySm" color="white">
              Camera is off
            </Text>
          </div>
        )}

        {preparationCountdown !== null && webcam.error == null && (
          <PreparationCountdownOverlay countdown={preparationCountdown} />
        )}
        <Overlay
          webcam={webcam}
          onToggleCamera={webcam.toggleCamera}
          cameraToggleDisabled={cameraToggleDisabled}
          microphoneWarning={showSilentAudioNudge}
        />

        <div role="status">{showSilentAudioNudge && <SilentAudioNudge />}</div>

        {webcam.error != null && (
          <div className={styles.messageOverlay}>
            <div className={styles.errorContainer}>
              <Text kind="systemMd">{webcam.error.message}</Text>
              <Spacer spacing={12} />
              <Button
                kind="primary"
                size="sm"
                onClick={() => webcam.startCamera()}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PreparationCountdownOverlay({
  countdown,
}: {
  countdown: CountdownMessage;
}) {
  const totalSeconds = Math.ceil(countdown.milliseconds / 1000);
  // For ≥60s the countdown ticks per-minute, so mm:ss would look frozen.
  // Use the message string ("2 minutes") instead; switch to exact seconds below 60s.
  const display = totalSeconds >= 60 ? countdown.message : String(totalSeconds);

  return (
    <div aria-hidden="true" className={styles.countdownOverlay}>
      <div className={styles.countdownOverlayContent}>
        <Text kind="displayOne" color="white">
          Starting in
        </Text>
        <Text kind="displayOne" color="white">
          {display}
        </Text>
      </div>
    </div>
  );
}
